/*
 * @Author: Yumeng Xue
 * @Date: 2025-11-18
 * @Description: 
 * "Messiness" WebGPU 渲染器
 * 使用 Pixi.js v8 WebGPU 后端进行高性能渲染
 */
import {
    Application,
    RenderTexture,
    Sprite,
    Graphics,
    Texture,
    isWebGPUSupported
} from 'pixi.js';
import { Viewport } from 'pixi-viewport';
import { Binning, LineData } from './binning';
import { TdaScanResult } from './tda-scanner';
import { StructureTensorScanResult } from './structure-tensor-scanner';
import {
    createHeatmapPixels,
    HeatmapMode,
    getCurrentColormap,
    HeatmapNormalizationOptions
} from '../utils/heatmap';
import { getColormapColor } from './colormaps';
import { LocalVectorization, AnchorInfo, FeatureVectorResult } from './local-vectorization';
import { AnalysisEngine, MSTResult, ProjectionResult, ClusteringResult } from './analysis-engine';
import { FocusContextFilter } from '../filters/FocusContextFilter';

export type { HeatmapMode };

export interface HeatmapRenderOptions {
    useHistogramEqualization?: boolean;
    normalizationOptions?: HeatmapNormalizationOptions;
}

function normalizeToUnitInterval(
    value: number,
    min: number,
    max: number,
    fallback: number = 0
): number {
    if (!isFinite(min) || !isFinite(max)) {
        return fallback;
    }

    const range = max - min;
    if (range <= 1e-10) {
        return fallback;
    }

    const normalized = (value - min) / range;
    return Math.max(0, Math.min(1, normalized));
}

export class WebGPUMessinessRenderer {
    private app: Application;
    private lines: LineData[];
    private allLineIds: Set<number>; // 所有线条ID集合
    private activeLineIds: Set<number>; // 当前活跃（未被剥离）的线条ID集合
    private binning: Binning;

    private viewport!: Viewport;
    private densityLayer!: Sprite;
    private contextLinesLayer!: Graphics;
    private focusLinesLayer!: Graphics;
    private selectionBoxLayer!: Graphics;
    private highlightLinesLayer!: Graphics;
    private densityTexture!: RenderTexture;
    private messinessTexture!: RenderTexture; // GPU: 存储messiness数据
    private densityDataTexture!: RenderTexture; // GPU: 存储归一化density数据

    private isDestroyed: boolean = false;
    private selectedLineIds: Set<number> = new Set();
    private focusedLineIds: Set<number> = new Set();
    private selectionBoxRects: { x: number; y: number; width: number; height: number }[] = [];
    private baseHeatmapAlpha: number = 1.0;
    private selectedLineOpacity: number = 0.5;

    private useHistogramEqualization: boolean = true;  // 是否使用直方图均衡化

    // Focus+Context Filter — Desaturation 策略
    private focusContextFilter?: FocusContextFilter;
    private focusContextParams = {
        threshold: 0.5,
        saturationDrop: 1.0,
        transition: 0.1,
        hatchDensity: 80.0,
        hatchThickness: 0.25,
        hatchOpacity: 0,
    };

    private constructor(lines: LineData[], binning: Binning, app: Application) {
        this.app = app;
        this.lines = lines;
        this.binning = binning;
        this.isDestroyed = false;

        // 初始化所有线条ID集合
        this.allLineIds = new Set(lines.map((_, idx) => idx));
        // 初始时所有线条都是活跃的
        this.activeLineIds = new Set(this.allLineIds);
    }

    /**
     * 异步工厂方法 - 使用 WebGPU 渲染器
     */
    public static async create(
        canvas: HTMLCanvasElement,
        lines: LineData[],
        binning: Binning
    ): Promise<WebGPUMessinessRenderer> {

        const canvasWidth = canvas.clientWidth;
        const canvasHeight = canvas.clientHeight;

        // 1. 检查 WebGPU 支持
        if (!navigator.gpu) {
            throw new Error('WebGPU is not supported in this browser. Please use Chrome/Edge 113+ or Safari 18+');
        }

        // 2. 异步初始化 Pixi Application with WebGPU
        const app = new Application();
        await app.init({
            view: canvas,
            width: canvasWidth,
            height: canvasHeight,
            backgroundColor: 0xFFFFFF,
            antialias: true,
            autoDensity: true,
            resolution: window.devicePixelRatio || 1,
            // 关键：指定使用 WebGPU 渲染器
            preference: 'webgpu',
            // WebGPU 特定选项
            powerPreference: 'high-performance', // 使用高性能 GPU
        });

        // 验证是否使用了 WebGPU
        console.log('Renderer type:', app.renderer.type); // 应该是 'webgpu'
        const hasWebGPU = await isWebGPUSupported();
        if (!hasWebGPU) {
            console.warn('WebGPU not available, falling back to WebGL');
        }

        // 3. 创建渲染器实例
        const renderer = new WebGPUMessinessRenderer(lines, binning, app);

        // 4. 设置 Viewport
        renderer.viewport = new Viewport({
            screenWidth: canvasWidth,
            screenHeight: canvasHeight,
            worldWidth: binning.width,
            worldHeight: binning.height,
            events: app.renderer.events,
            passiveWheel: false,
        });
        app.stage.addChild(renderer.viewport);

        renderer.viewport
            .drag()
            .pinch()
            .wheel({ trackpadPinch: true, wheelZoom: true })
            .decelerate();

        // Patch pixi-viewport's handleWheel to support negative scaling (scale.y = -1).
        // The original code uses `top <= y && y <= bottom` which fails when scale.y is
        // negative because top > bottom. We use Math.min/max to handle both cases.
        // See: https://github.com/pixijs-userland/pixi-viewport/issues/500
        const input = (renderer.viewport as any).input;
        if (input) {
            input.handleWheel = function (e: WheelEvent) {
                if (this.viewport.pause || !this.viewport.visible) return;
                const point = this.viewport.toLocal(this.getPointerPosition(e));
                const minX = Math.min(this.viewport.left, this.viewport.right);
                const maxX = Math.max(this.viewport.left, this.viewport.right);
                const minY = Math.min(this.viewport.top, this.viewport.bottom);
                const maxY = Math.max(this.viewport.top, this.viewport.bottom);
                if (minX <= point.x && point.x <= maxX && minY <= point.y && point.y <= maxY) {
                    if (this.viewport.plugins.wheel(e) && !this.viewport.options.passiveWheel) {
                        e.preventDefault();
                    }
                }
            };
        }

        // 5. 初始化图层
        const [binWidth, binHeight] = binning.getBinCounts();

        renderer.densityTexture = RenderTexture.create({
            width: binWidth,
            height: binHeight,
            resolution: 1, // 必须与 densityDataTexture/messinessTexture 保持 resolution 一致
        });

        renderer.densityLayer = new Sprite(renderer.densityTexture);
        renderer.densityLayer.width = binning.width;
        renderer.densityLayer.height = binning.height;
        renderer.densityTexture.source.scaleMode = 'nearest';

        renderer.contextLinesLayer = new Graphics();
        renderer.focusLinesLayer = new Graphics();
        renderer.selectionBoxLayer = new Graphics();
        renderer.highlightLinesLayer = new Graphics();

        // 6. 添加图层到 Viewport（顺序很重要：从底层到顶层）
        renderer.viewport.addChild(renderer.densityLayer);
        renderer.viewport.addChild(renderer.contextLinesLayer);
        renderer.viewport.addChild(renderer.focusLinesLayer);
        renderer.viewport.addChild(renderer.highlightLinesLayer);
        renderer.viewport.addChild(renderer.selectionBoxLayer);

        // 7. 翻转 Y 轴
        renderer.viewport.scale.y = -1;
        renderer.viewport.position.y = binning.height;

        // 8. 绘制上下文线条（使用 WebGPU 加速）
        console.log("WebGPU Renderer: Drawing context lines...");
        await renderer.drawContextLines();
        console.log("WebGPU Renderer: Context lines drawn.");

        return renderer;
    }

    /**
     * 绘制所有线条 - WebGPU 优化版本（仅绘制活跃线条）
     */
    private async drawContextLines() {
        if (this.isDestroyed) return;
        this.contextLinesLayer.clear();
        this.contextLinesLayer.alpha = 0.00;

        // 批量绘制优化（仅绘制活跃线条）
        const batchSize = 1000;
        const activeLines = this.lines.filter((_, idx) => this.activeLineIds.has(idx));

        for (let i = 0; i < activeLines.length; i += batchSize) {
            const batch = activeLines.slice(i, Math.min(i + batchSize, activeLines.length));

            for (const line of batch) {
                if (!line.xValues || line.xValues.length < 2) continue;

                this.contextLinesLayer.moveTo(line.xValues[0], line.yValues[0]);
                for (let j = 1; j < line.xValues.length; j++) {
                    this.contextLinesLayer.lineTo(line.xValues[j], line.yValues[j]);
                }
                this.contextLinesLayer.stroke({ width: 1, color: 0x1a5fb4, alpha: 1 });
            }

            // 让出控制权，避免阻塞主线程
            if (i + batchSize < activeLines.length) {
                await new Promise(resolve => setTimeout(resolve, 0));
            }
        }
    }

    /**
     * 更新热图 - WebGPU 优化版本（GPU Shader实现）
     */
    public async updateHeatmap(
        scanResult: TdaScanResult | StructureTensorScanResult,
        mode: HeatmapMode,
        options: HeatmapRenderOptions = {}
    ) {
        console.log("WebGPU Renderer: Updating heatmap texture...");
        const useHistogramEqualization = options.useHistogramEqualization ?? true;
        this.useHistogramEqualization = useHistogramEqualization;
        if (this.isDestroyed) return;

        const [binCountX, binCountY] = this.binning.getBinCounts();

        // GPU路径：FocusContext模式(原IlluminatedMessiness)使用shader
        if (mode === "IlluminatedMessiness" && 'coherenceScores' in scanResult && 'densityScores' in scanResult) {
            await this.updateHeatmapGPU(scanResult, binCountX, binCountY, options.normalizationOptions);
            this.setHeatmapVisible(true);
            return;
        }

        // CPU路径：其他模式使用原有实现
        const pixelData = createHeatmapPixels(
            scanResult,
            binCountX,
            binCountY,
            mode,
            -65, // shadingMagnitude (unused for non-illuminated modes but kept for API compat)
            Math.PI / 3,
            (5 * Math.PI) / 4,
            useHistogramEqualization,
            options.normalizationOptions
        );

        try {
            // 使用 ImageData 创建纹理
            const imageData = new ImageData(
                new Uint8ClampedArray(pixelData),
                binCountX,
                binCountY
            );

            // 创建临时 canvas
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = binCountX;
            tempCanvas.height = binCountY;
            const context = tempCanvas.getContext('2d');

            if (context) {
                context.putImageData(imageData, 0, 0);

                // 从 canvas 创建纹理（WebGPU 会自动优化）
                const tempTexture = Texture.from(tempCanvas);
                tempTexture.source.scaleMode = 'nearest';

                // 调整 RenderTexture 大小
                this.densityTexture.resize(binCountX, binCountY);

                // 渲染到 RenderTexture（WebGPU 会使用 GPU 加速）
                this.app.renderer.render({
                    container: new Sprite(tempTexture),
                    target: this.densityTexture
                });

                // 清理
                tempTexture.destroy(true);
                this.densityLayer.texture = this.densityTexture;
            }
        } catch (error) {
            console.error("WebGPU heatmap update failed:", error);
        }

        this.setHeatmapVisible(mode !== "Off");
    }

    /**
     * GPU加速的 Focus+Context 热图更新
     * 
     * 流程:
     * 1. CPU 端准备 density + messiness 归一化纹理数据
     * 2. 使用 Magma colormap 对 density 着色 → densityDataTexture
     * 3. 上传 messiness 单通道 → messinessTexture
     * 4. 通过 FocusContextFilter (WGSL) Overlay/Masking 遮罩杂乱区域
     * 5. 输出到 densityTexture → densityLayer
     */
    private async updateHeatmapGPU(
        scanResult: StructureTensorScanResult,
        width: number,
        height: number,
        normalizationOptions?: HeatmapNormalizationOptions
    ): Promise<void> {
        try {
            const { coherenceScores: mScores, densityScores: dScores } = scanResult;

            // 1. 计算归一化范围 (仅考虑 d>0 的有效像素，排除背景空白)
            const densityReference = normalizationOptions?.densityReference;
            const referenceDensityScores = densityReference && 'densityScores' in densityReference
                ? densityReference.densityScores
                : densityReference && 'dScores' in densityReference
                    ? densityReference.dScores
                    : dScores;
            let dMin = Infinity, dMax = -Infinity;
            let mMin = Infinity, mMax = -Infinity;

            for (let x = 0; x < width; x++) {
                for (let y = 0; y < height; y++) {
                    const referenceDensity = referenceDensityScores[x]?.[y] || 0;
                    const d = dScores[x][y];
                    const m = mScores[x][y];
                    if (referenceDensity > 0) {
                        dMin = Math.min(dMin, referenceDensity);
                        dMax = Math.max(dMax, referenceDensity);
                    }
                    if (d > 0) {
                        if (m > 0) { mMin = Math.min(mMin, m); mMax = Math.max(mMax, m); }
                    }
                }
            }
            // Fallback: 如果没有有效值
            if (!isFinite(dMin)) { dMin = 0; dMax = 1; }
            if (!isFinite(mMin)) { mMin = 0; mMax = 1; }
            const dRange = dMax - dMin + 1e-10;

            // 2. 处理 messiness 数据 (直方图均衡化 or 线性归一化)
            const densityData = new Float32Array(width * height);
            const messinessData = new Float32Array(width * height);

            if (this.useHistogramEqualization) {
                const validMValues: number[] = [];
                for (let x = 0; x < width; x++) {
                    for (let y = 0; y < height; y++) {
                        if (dScores[x][y] > 0 && mScores[x][y] > 0) {
                            validMValues.push(mScores[x][y]);
                        }
                    }
                }
                const sortedM = validMValues.length > 0
                    ? Float32Array.from(validMValues.sort((a, b) => a - b))
                    : new Float32Array(0);

                for (let x = 0; x < width; x++) {
                    for (let y = 0; y < height; y++) {
                        const idx = y * width + x;
                        const d = dScores[x][y];
                        densityData[idx] = d > 0 ? (d - dMin) / dRange : 0;

                        const m = mScores[x][y];
                        if (sortedM.length > 0 && d > 0) {
                            let left = 0, right = sortedM.length;
                            while (left < right) {
                                const mid = Math.floor((left + right) / 2);
                                if (sortedM[mid] <= m) left = mid + 1;
                                else right = mid;
                            }
                            messinessData[idx] = left / sortedM.length;
                        }
                    }
                }
	            } else {
	                for (let x = 0; x < width; x++) {
	                    for (let y = 0; y < height; y++) {
	                        const idx = y * width + x;
	                        const d = dScores[x][y];
	                        const m = mScores[x][y];
	                        densityData[idx] = d > 0 ? (d - dMin) / dRange : 0;
	                        messinessData[idx] = d > 0
	                            ? normalizeToUnitInterval(m, mMin, mMax, 1)
	                            : 0;
	                    }
	                }
	            }

            // 3. 将 density 着色为当前 colormap RGBA → densityDataTexture (uTexture)
            // 动态读取用户选择的 colormap，与 CPU 热图路径保持一致
            if (!this.densityDataTexture) {
                this.densityDataTexture = RenderTexture.create({ width, height, resolution: 1 });
                this.densityDataTexture.source.scaleMode = 'nearest';
            } else {
                this.densityDataTexture.resize(width, height);
            }

            {
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d')!;
                const imgData = ctx.createImageData(width, height);

                for (let i = 0; i < densityData.length; i++) {
                    const d = densityData[i];
                    if (d <= 0) {
                        // 空白区域 → 白色
                        imgData.data[i * 4] = 255;
                        imgData.data[i * 4 + 1] = 255;
                        imgData.data[i * 4 + 2] = 255;
                        imgData.data[i * 4 + 3] = 255;
                    } else {
                        // 使用当前系统 colormap 着色 (跟随用户选择)
                        const { colormap, reversed } = getCurrentColormap();
                        const [r, g, b] = getColormapColor(d, colormap, reversed);
                        imgData.data[i * 4] = r;
                        imgData.data[i * 4 + 1] = g;
                        imgData.data[i * 4 + 2] = b;
                        imgData.data[i * 4 + 3] = 255;
                    }
                }
                ctx.putImageData(imgData, 0, 0);

                const tempTex = Texture.from(canvas);
                this.app.renderer.render({ container: new Sprite(tempTex), target: this.densityDataTexture });
                tempTex.destroy(true);
            }

            // 4. 上传 messiness 单通道纹理
            if (!this.messinessTexture) {
                this.messinessTexture = RenderTexture.create({ width, height, resolution: 1 });
                this.messinessTexture.source.scaleMode = 'nearest';
            } else {
                this.messinessTexture.resize(width, height);
            }

            {
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d')!;
                const imgData = ctx.createImageData(width, height);
                for (let i = 0; i < messinessData.length; i++) {
                    const val = Math.floor(messinessData[i] * 255);
                    imgData.data[i * 4] = val;
                    imgData.data[i * 4 + 1] = val;
                    imgData.data[i * 4 + 2] = val;
                    imgData.data[i * 4 + 3] = 255;
                }
                ctx.putImageData(imgData, 0, 0);

                const tempTex = Texture.from(canvas);
                this.app.renderer.render({ container: new Sprite(tempTex), target: this.messinessTexture });
                tempTex.destroy(true);
            }

            // 5. 创建或更新 FocusContextFilter
            if (!this.focusContextFilter) {
                this.focusContextFilter = new FocusContextFilter({
                    messinessTexture: this.messinessTexture,
                    threshold: this.focusContextParams.threshold,
                    saturationDrop: this.focusContextParams.saturationDrop,
                    transition: this.focusContextParams.transition,
                    hatchDensity: this.focusContextParams.hatchDensity,
                    hatchThickness: this.focusContextParams.hatchThickness,
                    hatchOpacity: this.focusContextParams.hatchOpacity,
                });
            } else {
                this.focusContextFilter.updateMessinessTexture(this.messinessTexture);
                this.focusContextFilter.updateParams(this.focusContextParams);
            }

            // 6. 应用 filter: densityDataTexture → densityTexture (最终输出)
            //    关键: 三张纹理 (densityDataTexture, messinessTexture, densityTexture)
            //    必须全部使用 resolution=1, 否则 Pixi filter 系统计算的
            //    uInputSize/uOutputFrame 会因 DPR 缩放导致 UV 偏移
            this.densityTexture.resize(width, height);
            this.densityTexture.source.scaleMode = 'nearest';

            const filterSprite = new Sprite(this.densityDataTexture);
            filterSprite.width = width;
            filterSprite.height = height;
            filterSprite.filters = [this.focusContextFilter];

            try {
                this.app.renderer.render({
                    container: filterSprite,
                    target: this.densityTexture
                });
            } catch (e) {
                console.error('[FocusContext] Filter render failed:', e);
            }

            this.densityLayer.texture = this.densityTexture;
        } catch (error) {
            console.error('[FocusContext] updateHeatmapGPU FAILED:', error);
        }
    }

    /**
     * 绘制焦点线条
     */
    public drawFocus(focusLineIds: number[]) {
        if (this.isDestroyed) return;
        this.focusedLineIds = new Set(focusLineIds);
        this.focusLinesLayer.clear();
        this.focusLinesLayer.alpha = 1.0;

        for (const id of focusLineIds) {
            const line = this.lines[id];
            if (!line || !line.xValues || line.xValues.length < 2) continue;

            this.focusLinesLayer.moveTo(line.xValues[0], line.yValues[0]);
            for (let i = 1; i < line.xValues.length; i++) {
                this.focusLinesLayer.lineTo(line.xValues[i], line.yValues[i]);
            }
            this.focusLinesLayer.stroke({ width: 1.5, color: 0xFF0000, alpha: 1 });
        }
    }

    /**
     * 清除高亮/焦点线条
     * */
    public clearFocus() {
        if (this.isDestroyed) return;
        this.focusedLineIds.clear();
        this.focusLinesLayer.clear();
    }

    public setContextLinesAlpha(opacity: number) {
        if (this.isDestroyed) return;
        const clamped = Math.max(0, Math.min(0.5, opacity));
        this.contextLinesLayer.alpha = clamped;
    }

    public setHeatmapVisible(visible: boolean) {
        if (this.isDestroyed) return;
        this.densityLayer.visible = visible;
    }

    public setHeatmapAlpha(alpha: number) {
        if (this.isDestroyed) return;
        const clamped = Math.max(0, Math.min(1, alpha));
        this.densityLayer.alpha = clamped;
        this.baseHeatmapAlpha = clamped;
    }

    /** 设置 Focus+Context 参数 (仅更新 uniform, 不触发重新计算) */
    public setFocusContextParams(params: {
        threshold?: number;
        saturationDrop?: number;
        transition?: number;
        hatchDensity?: number;
        hatchThickness?: number;
        hatchOpacity?: number;
    }) {
        if (params.threshold !== undefined) this.focusContextParams.threshold = params.threshold;
        if (params.saturationDrop !== undefined) this.focusContextParams.saturationDrop = params.saturationDrop;
        if (params.transition !== undefined) this.focusContextParams.transition = params.transition;
        if (params.hatchDensity !== undefined) this.focusContextParams.hatchDensity = params.hatchDensity;
        if (params.hatchThickness !== undefined) this.focusContextParams.hatchThickness = params.hatchThickness;
        if (params.hatchOpacity !== undefined) this.focusContextParams.hatchOpacity = params.hatchOpacity;

        // 同步更新 GPU filter uniform (如果已创建)
        if (this.focusContextFilter) {
            this.focusContextFilter.updateParams(this.focusContextParams);
        }
    }

    /** 获取当前 Focus+Context 参数 */
    public getFocusContextParams() {
        return { ...this.focusContextParams };
    }

    /**
     * 重新渲染 Focus+Context 滤镜 (参数变更后调用)
     * 仅重新运行 fragment shader, 不重新计算 messiness 数据
     */
    public reapplyFocusContextFilter(): void {
        if (!this.focusContextFilter || !this.densityDataTexture) return;

        this.focusContextFilter.updateParams(this.focusContextParams);

        const filterSprite = new Sprite(this.densityDataTexture);
        filterSprite.width = this.densityDataTexture.width;
        filterSprite.height = this.densityDataTexture.height;
        filterSprite.filters = [this.focusContextFilter];

        try {
            this.app.renderer.render({
                container: filterSprite,
                target: this.densityTexture
            });
        } catch (e) {
            console.error('[FocusContext] reapply failed:', e);
        }

        this.densityLayer.texture = this.densityTexture;
    }

    /**
     * 提取矩形区域内的线条 ID（基于 Bin 索引的高效实现）
     * 
     * 策略：
     * 1. 将世界坐标矩形映射到 Bin 坐标范围
     * 2. 遍历矩形覆盖的 Bin，收集所有穿过这些 Bin 的线条 ID
     * 3. 额外检查起点/终点落在矩形内的线条
     * 4. 仅返回活跃线条
     * 
     * @param worldRect - 世界坐标系中的矩形 { x, y, width, height }
     * @param timeBox - 是否启用 TimeBox 模式（仅保留从左边缘进入、右边缘离开的线条）
     * @returns 与矩形相交的活跃线条 ID 数组
     */
    public extractLinesInRect(
        worldRect: { x: number; y: number; width: number; height: number },
        timeBox: boolean = false
    ): number[] {
        if (this.isDestroyed) return [];

        const { x: rectX, y: rectY, width: rectWidth, height: rectHeight } = worldRect;
        const rectRight = rectX + rectWidth;
        const rectBottom = rectY + rectHeight;

        const [binW, binH] = this.binning.getBinSize();
        const [binCountX, binCountY] = this.binning.getBinCounts();
        const bins = this.binning.getBins();

        // 1. 将世界坐标矩形映射到 Bin 坐标范围
        const binXMin = Math.max(0, Math.floor(rectX / binW));
        const binXMax = Math.min(binCountX - 1, Math.floor(rectRight / binW));
        const binYMin = Math.max(0, Math.floor(rectY / binH));
        const binYMax = Math.min(binCountY - 1, Math.floor(rectBottom / binH));

        // 2. 收集所有内部 Bin 中的线条 ID（穿过矩形内部的线条）
        const allLineIds = new Set<number>();
        for (let bx = binXMin; bx <= binXMax; bx++) {
            for (let by = binYMin; by <= binYMax; by++) {
                const bin = bins[bx][by];
                for (const lid of bin.lineIds) {
                    if (this.activeLineIds.has(lid)) {
                        allLineIds.add(lid);
                    }
                }
            }
        }

        // 3. 额外检查：起点或终点落在矩形内的活跃线条
        for (const lineId of this.activeLineIds) {
            if (allLineIds.has(lineId)) continue; // 已经通过 Bin 找到了
            const line = this.lines[lineId];
            if (!line || !line.xValues || line.xValues.length < 2) continue;

            // 检查起点
            const sx = line.xValues[0], sy = line.yValues[0];
            if (sx >= rectX && sx <= rectRight && sy >= rectY && sy <= rectBottom) {
                allLineIds.add(lineId);
                continue;
            }
            // 检查终点
            const ex = line.xValues[line.xValues.length - 1];
            const ey = line.yValues[line.yValues.length - 1];
            if (ex >= rectX && ex <= rectRight && ey >= rectY && ey <= rectBottom) {
                allLineIds.add(lineId);
            }
        }

        if (!timeBox) {
            return Array.from(allLineIds);
        }

        // ================================================================
        // TimeBox 模式：精确路径追踪验证
        // 
        // 线条必须满足以下所有条件才被保留：
        // 1. 从矩形的左边缘 (x=rectX) 进入
        // 2. 从矩形的右边缘 (x=rectRight) 离开
        // 3. 在左边缘进入到右边缘离开之间，y 值始终在 [rectY, rectBottom] 内
        //    即线条不能中途从上方或下方离开矩形
        // ================================================================

        const result: number[] = [];

        for (const lineId of allLineIds) {
            const line = this.lines[lineId];
            if (!line || !line.xValues || line.xValues.length < 2) continue;

            if (this.isTimeBoxValid(line, rectX, rectRight, rectY, rectBottom)) {
                result.push(lineId);
            }
        }

        return result;
    }

    /**
     * TimeBox 精确验证：检查一条线是否严格从左边缘进入、右边缘离开，
     * 且在矩形内的路径段中 y 值始终在 [rectY, rectBottom] 范围内。
     */
    private isTimeBoxValid(
        line: LineData,
        rectLeft: number, rectRight: number,
        rectTop: number, rectBottom: number
    ): boolean {
        const xs = line.xValues;
        const ys = line.yValues;
        const n = xs.length;

        // 状态机：追踪线条是否已从左边缘进入矩形
        let enteredFromLeft = false;
        let exitedFromRight = false;
        let insideRect = false; // 当前是否在矩形内部

        for (let i = 0; i < n; i++) {
            const x = xs[i];
            const y = ys[i];
            const inBox = x >= rectLeft && x <= rectRight && y >= rectTop && y <= rectBottom;

            if (i === 0) {
                // 起点就在矩形内 → 它不是从左边缘"进入"的（除非起点恰在左边缘）
                if (inBox) {
                    // 起点在左边缘上视为有效进入
                    if (x <= rectLeft + (rectRight - rectLeft) * 0.01) {
                        enteredFromLeft = true;
                    }
                    insideRect = true;
                }
                continue;
            }

            const prevX = xs[i - 1];
            const prevY = ys[i - 1];

            // --- 检测进入事件 ---
            if (!insideRect && inBox) {
                // 从外部进入矩形
                // 通过插值判断进入边缘：线段 (prevX,prevY)→(x,y) 首先穿越哪条边
                // 如果从左边缘进入 (prevX < rectLeft, x >= rectLeft)
                if (prevX < rectLeft && x >= rectLeft) {
                    // 计算穿越左边缘时的 y
                    const t = (rectLeft - prevX) / (x - prevX);
                    const crossY = prevY + t * (y - prevY);
                    if (crossY >= rectTop && crossY <= rectBottom) {
                        enteredFromLeft = true;
                        insideRect = true;
                    }
                }
                if (!insideRect && inBox) {
                    // 从其他边缘进入（上/下/右）→ 不是有效的 TimeBox 进入
                    // 但我们仍需追踪状态
                    insideRect = true;
                    // 从非左边缘进入 → 若之前已从左进入过，说明中途离开又回来了
                    // 这种情况需要在离开时处理
                }
            }

            // --- 检测在矩形内但 y 越界 ---
            if (insideRect && enteredFromLeft) {
                if (y < rectTop || y > rectBottom) {
                    // 线条在矩形 x 范围内但 y 超出了上下边界 → 无效
                    return false;
                }
            }

            // --- 检测离开事件 ---
            if (insideRect && !inBox) {
                if (!enteredFromLeft) {
                    // 从非左边缘进入又离开 → 忽略这段
                    insideRect = false;
                    continue;
                }

                // 已从左边缘进入，现在离开了
                // 判断从哪条边离开
                if (x > rectRight) {
                    // 从右边缘离开 → 有效！
                    // 但要验证穿越右边缘时 y 在范围内
                    const t = (rectRight - prevX) / (x - prevX);
                    const crossY = prevY + t * (y - prevY);
                    if (crossY >= rectTop && crossY <= rectBottom) {
                        exitedFromRight = true;
                        // 成功从左进右出，且中间没有 y 越界
                        return true;
                    }
                }
                // 从上方、下方或左方离开 → 无效
                return false;
            }
        }

        // 线条在遍历完所有点后仍在矩形内（终点在矩形内）
        if (insideRect && enteredFromLeft) {
            // 终点在右边缘附近视为有效
            const lastX = xs[n - 1];
            if (lastX >= rectRight - (rectRight - rectLeft) * 0.01) {
                return true;
            }
            // 终点在矩形内但没到右边缘 → 不算贯穿
            return false;
        }

        return exitedFromRight;
    }

    /**
     * 高亮显示选中的线条
     * @param lineIds - 要高亮的线条 ID 数组
     * @param dimHeatmap - 是否降低热图透明度
     */
    public highlightLines(lineIds: number[], dimHeatmap: boolean = true, opacity: number = 0.5) {
        if (this.isDestroyed) return;

        this.selectedLineIds = new Set(lineIds);
        this.selectedLineOpacity = Math.max(0, Math.min(1, opacity));

        // 清空高亮图层
        this.highlightLinesLayer.clear();

        if (lineIds.length === 0) {
            // 如果没有选中的线条，恢复热图透明度
            this.densityLayer.alpha = this.baseHeatmapAlpha;
            return;
        }

        // 降低热图透明度
        if (dimHeatmap) {
            this.densityLayer.alpha = this.baseHeatmapAlpha * 0.3;
        }

        // 绘制高亮线条
        this.highlightLinesLayer.alpha = 1.0;

        for (const lineId of lineIds) {
            const line = this.lines[lineId];
            if (!line || !line.xValues || line.xValues.length < 2) continue;

            this.highlightLinesLayer.moveTo(line.xValues[0], line.yValues[0]);
            for (let i = 1; i < line.xValues.length; i++) {
                this.highlightLinesLayer.lineTo(line.xValues[i], line.yValues[i]);
            }
            this.highlightLinesLayer.stroke({ width: 1.2, color: 0xFF6B00, alpha: this.selectedLineOpacity });
        }

        console.log(`Highlighted ${lineIds.length} lines`);
    }

    /**
     * 清除高亮
     */
    public clearHighlight() {
        if (this.isDestroyed) return;
        this.selectedLineIds.clear();
        this.highlightLinesLayer.clear();
        this.densityLayer.alpha = this.baseHeatmapAlpha;
    }

    private drawLineSetToCanvas(
        ctx: CanvasRenderingContext2D,
        lineIds: Iterable<number>,
        style: { width: number; color: string; alpha: number }
    ) {
        if (style.alpha <= 0) return;

        ctx.save();
        ctx.globalAlpha = style.alpha;
        ctx.strokeStyle = style.color;
        ctx.lineWidth = style.width;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';

        for (const lineId of lineIds) {
            const line = this.lines[lineId];
            if (!line || !line.xValues || line.xValues.length < 2) continue;

            ctx.beginPath();
            ctx.moveTo(line.xValues[0], line.yValues[0]);

            for (let i = 1; i < line.xValues.length; i++) {
                ctx.lineTo(line.xValues[i], line.yValues[i]);
            }

            ctx.stroke();
        }

        ctx.restore();
    }

    private getExportResolutionScale(worldWidth: number, worldHeight: number): number {
        const hasVectorLines =
            this.contextLinesLayer.alpha > 0 ||
            this.focusedLineIds.size > 0 ||
            this.selectedLineIds.size > 0;

        if (!hasVectorLines) {
            return 1;
        }

        const canvas = this.app.canvas;
        const canvasPixelWidth = canvas.width || canvas.clientWidth || worldWidth;
        const canvasPixelHeight = canvas.height || canvas.clientHeight || worldHeight;
        const viewportScaleX = Math.abs(this.viewport.scale.x) || 1;
        const viewportScaleY = Math.abs(this.viewport.scale.y) || 1;
        const deviceScale = window.devicePixelRatio || 1;

        // 当前屏幕上每个世界单位对应的像素数；导出至少匹配这个密度，
        // 这样整图导出的线条不会明显比当前交互视图更糊。
        const pixelsPerWorldX = (canvasPixelWidth * viewportScaleX) / Math.max(worldWidth, 1);
        const pixelsPerWorldY = (canvasPixelHeight * viewportScaleY) / Math.max(worldHeight, 1);
        const desiredScale = Math.max(2, deviceScale, pixelsPerWorldX, pixelsPerWorldY);

        // 限制输出尺寸，避免超大画布导致浏览器内存压力过高。
        const maxEdge = 8192;
        const maxPixels = 32_000_000;
        const edgeLimitedScale = Math.min(maxEdge / Math.max(worldWidth, 1), maxEdge / Math.max(worldHeight, 1));
        const pixelLimitedScale = Math.sqrt(maxPixels / Math.max(worldWidth * worldHeight, 1));

        return Math.max(1, Math.min(desiredScale, edgeLimitedScale, pixelLimitedScale));
    }

    /**
     * 绘制选择框
     * @param worldRect - 世界坐标系中的矩形
     */
    public drawSelectionBox(worldRect: { x: number; y: number; width: number; height: number } | null) {
        if (this.isDestroyed) return;
        this.drawSelectionBoxes(worldRect ? [worldRect] : []);
    }

    /**
     * 绘制一个或多个选择框
     * @param worldRects - 世界坐标系中的矩形数组
     */
    public drawSelectionBoxes(worldRects: { x: number; y: number; width: number; height: number }[]) {
        if (this.isDestroyed) return;
        this.selectionBoxRects = worldRects.map(rect => ({ ...rect }));
        this.selectionBoxLayer.clear();

        if (worldRects.length === 0) return;

        worldRects.forEach((worldRect, index) => {
            const { x, y, width, height } = worldRect;
            const isLatest = index === worldRects.length - 1;
            const strokeAlpha = isLatest ? 0.85 : 0.55;
            const fillAlpha = isLatest ? 0.1 : 0.06;

            // 绘制选择框边框
            this.selectionBoxLayer.rect(x, y, width, height);
            this.selectionBoxLayer.stroke({ width: 2, color: 0x00AAFF, alpha: strokeAlpha });

            // 绘制半透明填充
            this.selectionBoxLayer.rect(x, y, width, height);
            this.selectionBoxLayer.fill({ color: 0x00AAFF, alpha: fillAlpha });
        });
    }

    /**
     * 获取 Viewport 引用（用于坐标转换）
     */
    public getViewport(): Viewport {
        return this.viewport;
    }

    public async getImageUrl(): Promise<string> {
        if (this.isDestroyed) return "";
        try {
            return await this.app.renderer.extract.base64(this.app.stage);
        } catch (error) {
            console.error("Export failed:", error);
            return "";
        }
    }

    /**
     * 导出完整的原始分辨率canvas图像
     * 此方法会导出完整的世界坐标视图，而不受当前viewport缩放和平移的影响
     * @returns Promise<string> - Base64 encoded PNG image data URL
     */
    public async exportFullCanvas(): Promise<string> {
        if (this.isDestroyed) {
            throw new Error("Renderer has been destroyed");
        }

        try {
            const worldWidth = this.binning.width;
            const worldHeight = this.binning.height;

            if (worldWidth <= 0 || worldHeight <= 0) {
                throw new Error(`Invalid export size: ${worldWidth}x${worldHeight}`);
            }

            const exportScale = this.getExportResolutionScale(worldWidth, worldHeight);
            const exportWidth = Math.max(1, Math.round(worldWidth * exportScale));
            const exportHeight = Math.max(1, Math.round(worldHeight * exportScale));
            const scaleX = exportWidth / worldWidth;
            const scaleY = exportHeight / worldHeight;

            const canvas = document.createElement('canvas');
            canvas.width = exportWidth;
            canvas.height = exportHeight;

            const ctx = canvas.getContext('2d');
            if (!ctx) {
                throw new Error('Failed to create 2D canvas context');
            }

            // 避开 Pixi WebGPU 的离屏 Graphics 渲染路径，直接用 2D canvas 复合各图层。
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, exportWidth, exportHeight);

            ctx.save();
            ctx.translate(0, exportHeight);
            ctx.scale(scaleX, -scaleY);

            if (this.densityLayer.visible) {
                const heatmapCanvas = this.app.renderer.extract.canvas(this.densityTexture);

                if (heatmapCanvas) {
                    ctx.save();
                    ctx.globalAlpha = this.densityLayer.alpha;
                    ctx.imageSmoothingEnabled = false;
                    ctx.drawImage(heatmapCanvas as unknown as CanvasImageSource, 0, 0, worldWidth, worldHeight);
                    ctx.restore();
                }
            }

            if (this.contextLinesLayer.alpha > 0) {
                this.drawLineSetToCanvas(ctx, this.activeLineIds, {
                    width: 1,
                    color: '#1a5fb4',
                    alpha: this.contextLinesLayer.alpha,
                });
            }

            if (this.focusedLineIds.size > 0 && this.focusLinesLayer.alpha > 0) {
                this.drawLineSetToCanvas(ctx, this.focusedLineIds, {
                    width: 1.5,
                    color: '#ff0000',
                    alpha: this.focusLinesLayer.alpha,
                });
            }

            if (this.selectedLineIds.size > 0 && this.selectedLineOpacity > 0) {
                this.drawLineSetToCanvas(ctx, this.selectedLineIds, {
                    width: 1.2,
                    color: '#ff6b00',
                    alpha: this.selectedLineOpacity,
                });
            }

            if (this.selectionBoxRects.length > 0) {
                this.selectionBoxRects.forEach(({ x, y, width, height }, index) => {
                    const isLatest = index === this.selectionBoxRects.length - 1;

                    ctx.save();
                    ctx.globalAlpha = isLatest ? 0.1 : 0.06;
                    ctx.fillStyle = '#00aaff';
                    ctx.fillRect(x, y, width, height);
                    ctx.globalAlpha = isLatest ? 0.85 : 0.55;
                    ctx.strokeStyle = '#00aaff';
                    ctx.lineWidth = 2;
                    ctx.strokeRect(x, y, width, height);
                    ctx.restore();
                });
            }

            ctx.restore();

            return canvas.toDataURL('image/png');
        } catch (error) {
            console.error('[Export] Export failed:', error);
            throw new Error(`Failed to export canvas: ${error}`);
        }
    }

    public screenToBin(screenX: number, screenY: number): { binX: number, binY: number } | null {
        if (this.isDestroyed) return null;

        const worldPoint = this.viewport.toWorld(screenX, screenY);
        const [binWidth, binHeight] = this.binning.getBinSize();
        const binX = Math.floor(worldPoint.x / binWidth);
        const binY = Math.floor(worldPoint.y / binHeight);

        const [binCountX, binCountY] = this.binning.getBinCounts();
        if (binX < 0 || binX >= binCountX || binY < 0 || binY >= binCountY) {
            return null;
        }

        return { binX, binY };
    }

    /**
     * 提取选中线条的局部特征向量
     * 
     * @param worldRect - 世界坐标系中的选择矩形
     * @param w - 窗口半径
     * @param sampleCount - 采样点数量
     * @param mode - 模式（1D 或 2D）
     * @param normalize - 是否归一化
     * @returns 特征向量结果
     */
    public extractFeatureVectors(
        worldRect: { x: number; y: number; width: number; height: number },
        w: number,
        sampleCount: number,
        mode: '1D_TimeSeries' | '2D_Trajectory',
        normalize: boolean = true
    ): FeatureVectorResult | null {
        if (this.isDestroyed) return null;

        // 1. 提取选择区域内的线条
        const selectedLineIds = this.extractLinesInRect(worldRect);

        if (selectedLineIds.length === 0) {
            console.warn('No lines found in selection');
            return null;
        }

        // 2. 为每条线找到锚点（选择区域的中心点）
        const anchors = new Map<number, AnchorInfo>();
        const rectCenterX = worldRect.x + worldRect.width / 2;
        const rectCenterY = worldRect.y + worldRect.height / 2;

        for (const lineId of selectedLineIds) {
            const line = this.lines[lineId];
            if (!line || !line.xValues || line.xValues.length < 2) continue;

            // 找到离矩形中心最近的点作为锚点
            let minDist = Infinity;
            let anchorIndex = 0;

            for (let i = 0; i < line.xValues.length; i++) {
                const dx = line.xValues[i] - rectCenterX;
                const dy = line.yValues[i] - rectCenterY;
                const dist = dx * dx + dy * dy;

                if (dist < minDist) {
                    minDist = dist;
                    anchorIndex = i;
                }
            }

            // 计算锚点的时间/弧长值（如果需要）
            const anchorInfo: AnchorInfo = {
                lineId,
                index: anchorIndex
            };

            if (mode === '1D_TimeSeries' && line.times) {
                anchorInfo.t = line.times[anchorIndex];
            } else if (mode === '1D_TimeSeries') {
                anchorInfo.t = line.xValues[anchorIndex];
            }

            anchors.set(lineId, anchorInfo);
        }

        // 3. 使用 LocalVectorization 提取特征向量
        const result = LocalVectorization.getFeatureVectors(
            this.lines,
            selectedLineIds,
            anchors,
            w,
            sampleCount,
            mode,
            normalize
        );

        console.log(`Extracted feature vectors for ${result.lineIds.length} lines`);
        console.log(`Vector dimension: ${result.metadata.vectorDim}`);

        return result;
    }

    /**
     * 对选中线条进行完整的拓扑分析
     * 
     * 包括：
     * 1. 提取特征向量
     * 2. 计算 MST
     * 3. 自动聚类
     * 4. UMAP 降维（可选）
     * 
     * @param worldRect - 世界坐标系中的选择矩形
     * @param w - 窗口半径
     * @param sampleCount - 采样点数量
     * @param mode - 模式（1D 或 2D）
     * @param runUMAP - 是否执行 UMAP 降维
     * @returns 完整的分析结果
     */
    public analyzeSelection(
        worldRect: { x: number; y: number; width: number; height: number },
        w: number,
        sampleCount: number,
        mode: '1D_TimeSeries' | '2D_Trajectory',
        runUMAP: boolean = true
    ): {
        features: FeatureVectorResult | null;
        mst: MSTResult | null;
        clustering: ClusteringResult | null;
        projection: ProjectionResult | null;
    } {
        console.log('=== Starting Topological Analysis ===');

        // 1. 提取特征向量
        const features = this.extractFeatureVectors(worldRect, w, sampleCount, mode, true);

        if (!features || features.vectors.length < 2) {
            console.warn('Insufficient lines for analysis');
            return { features, mst: null, clustering: null, projection: null };
        }

        // 2. 使用 K-Means 聚类（自动确定聚类数，最多8个）
        console.log('Determining optimal cluster count...');
        const suggestedK = AnalysisEngine.suggestKMeansK(features.vectors, 8);
        console.log(`Suggested K: ${suggestedK}`);

        console.log('Performing K-Means clustering...');
        const clustering = AnalysisEngine.clusterByKMeans(features.vectors, suggestedK);

        console.log(`Clustering result: ${clustering.numClusters} clusters`);
        console.log(`Cluster labels:`, clustering.labels);

        // 3. 计算 MST（用于拓扑分析）
        console.log('Computing MST for topology...');
        const mst = AnalysisEngine.computeMST(features.vectors);

        // 4. UMAP 降维（可选）
        let projection: ProjectionResult | null = null;
        if (runUMAP) {
            console.log('Running UMAP projection...');
            projection = AnalysisEngine.runProjection(features.vectors, 15, 0.1, 200);
            console.log(`UMAP projection completed: ${projection.coordinates.length} points`);
        }

        console.log('=== Analysis Complete ===');

        return { features, mst, clustering, projection };
    }

    /**
     * 高亮特定聚类，降低其他聚类的不透明度
     * 
     * @param lineIds - 所有线条 ID 列表
     * @param clusterLabels - 聚类标签
     * @param highlightClusterId - 要高亮的聚类 ID（null 表示全部显示）
     * @param colorPalette - 颜色调色板（可选）
     */
    public highlightSpecificCluster(
        lineIds: number[],
        clusterLabels: number[],
        highlightClusterId: number | null,
        colorPalette?: number[]
    ) {
        if (this.isDestroyed) return;
        if (lineIds.length !== clusterLabels.length) {
            console.error('lineIds and clusterLabels must have the same length');
            return;
        }

        // 默认颜色调色板
        const defaultPalette = [
            0xFF6B6B,  // 红色
            0x4ECDC4,  // 青色
            0xFFE66D,  // 黄色
            0x95E1D3,  // 薄荷绿
            0xF38181,  // 粉红色
            0xAA96DA,  // 紫色
            0xFCBF49,  // 橙色
            0x06D6A0,  // 翠绿色
            0x118AB2,  // 蓝色
            0xEF476F,  // 深粉色
        ];

        const palette = colorPalette || defaultPalette;

        // 清空高亮图层
        this.highlightLinesLayer.clear();
        this.selectedLineIds = new Set(lineIds);

        // 降低热图透明度
        this.densityLayer.alpha = this.baseHeatmapAlpha * 0.3;

        // 如果没有指定高亮聚类，显示所有聚类（正常模式）
        if (highlightClusterId === null) {
            for (let i = 0; i < lineIds.length; i++) {
                const lineId = lineIds[i];
                const clusterLabel = clusterLabels[i];

                // 跳过未分配聚类的线条（标签为-1）
                if (clusterLabel < 0) continue;

                const color = palette[clusterLabel % palette.length];

                const line = this.lines[lineId];
                if (!line || !line.xValues || line.xValues.length < 2) continue;

                this.highlightLinesLayer.moveTo(line.xValues[0], line.yValues[0]);
                for (let j = 1; j < line.xValues.length; j++) {
                    this.highlightLinesLayer.lineTo(line.xValues[j], line.yValues[j]);
                }
                this.highlightLinesLayer.stroke({ width: 1.5, color: color, alpha: 1.0 });
            }
        } else {
            // 高亮模式：先绘制非高亮聚类（低透明度），再绘制高亮聚类

            // 第一遍：绘制非高亮聚类（透明度 0.15）
            for (let i = 0; i < lineIds.length; i++) {
                const lineId = lineIds[i];
                const clusterLabel = clusterLabels[i];

                // 跳过未分配聚类的线条（标签为-1）
                if (clusterLabel < 0) continue;

                if (clusterLabel !== highlightClusterId) {
                    const color = palette[clusterLabel % palette.length];
                    const line = this.lines[lineId];
                    if (!line || !line.xValues || line.xValues.length < 2) continue;

                    this.highlightLinesLayer.moveTo(line.xValues[0], line.yValues[0]);
                    for (let j = 1; j < line.xValues.length; j++) {
                        this.highlightLinesLayer.lineTo(line.xValues[j], line.yValues[j]);
                    }
                    this.highlightLinesLayer.stroke({ width: 1.5, color: color, alpha: 0.15 });
                }
            }

            // 第二遍：绘制高亮聚类（完全不透明，加粗）
            for (let i = 0; i < lineIds.length; i++) {
                const lineId = lineIds[i];
                const clusterLabel = clusterLabels[i];

                // 跳过未分配聚类的线条（标签为-1）
                if (clusterLabel < 0) continue;

                if (clusterLabel === highlightClusterId) {
                    const color = palette[clusterLabel % palette.length];
                    const line = this.lines[lineId];
                    if (!line || !line.xValues || line.xValues.length < 2) continue;

                    this.highlightLinesLayer.moveTo(line.xValues[0], line.yValues[0]);
                    for (let j = 1; j < line.xValues.length; j++) {
                        this.highlightLinesLayer.lineTo(line.xValues[j], line.yValues[j]);
                    }
                    this.highlightLinesLayer.stroke({ width: 2.5, color: color, alpha: 1.0 });
                }
            }
        }
    }

    /**
     * 根据聚类结果高亮显示不同集群
     * 
     * @param lineIds - 线条 ID 列表
     * @param clusterLabels - 聚类标签 [0, 1, 1, 0, 2, ...]
     * @param colorPalette - 颜色调色板（可选）
     */
    public highlightClusters(
        lineIds: number[],
        clusterLabels: number[],
        colorPalette?: number[]
    ) {
        // 调用新方法，highlightClusterId 为 null 表示显示全部
        this.highlightSpecificCluster(lineIds, clusterLabels, null, colorPalette);
    }

    /**
     * 获取当前选中的线条 ID 列表
     */
    public getSelectedLineIds(): number[] {
        return Array.from(this.selectedLineIds);
    }

    /**
     * 设置活跃线条ID集合（用于剥离聚类后更新可见线条）
     */
    public async setActiveLineIds(activeIds: Set<number>): Promise<void> {
        this.activeLineIds = new Set(activeIds);
        console.log(`Active lines updated: ${this.activeLineIds.size} / ${this.allLineIds.size}`);

        // 重新绘制线条层（仅显示活跃线条）
        await this.drawContextLines();
    }

    /**
     * 获取当前活跃线条ID列表
     */
    public getActiveLineIds(): number[] {
        return Array.from(this.activeLineIds);
    }

    /**
     * 获取所有线条ID列表
     */
    public getAllLineIds(): number[] {
        return Array.from(this.allLineIds);
    }

    /**
     * 渲染指定线条集合到 DataURL（用于生成聚类缩略图）
     * @param lineIds 要渲染的线条ID数组
     * @param width 输出图像宽度（默认使用 binning 分辨率）
     * @param height 输出图像高度（默认使用 binning 分辨率）
     * @returns PNG DataURL
     */
    public async renderLinesToDataURL(
        lineIds: number[],
        width?: number,
        height?: number
    ): Promise<string> {
        const worldWidth = this.binning.width;
        const worldHeight = this.binning.height;
        const targetWidth = Math.max(1, Math.round(width || worldWidth));
        const targetHeight = Math.max(1, Math.round(height || worldHeight));
        const scaleX = targetWidth / Math.max(worldWidth, 1);
        const scaleY = targetHeight / Math.max(worldHeight, 1);

        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return '';
        }

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, targetWidth, targetHeight);
        ctx.strokeStyle = 'rgba(15, 23, 42, 0.82)';
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.lineWidth = 1.1;

        for (const lineId of lineIds) {
            const line = this.lines[lineId];
            if (!line || !line.xValues || line.xValues.length < 2) continue;

            ctx.beginPath();
            ctx.moveTo(line.xValues[0] * scaleX, targetHeight - line.yValues[0] * scaleY);

            for (let j = 1; j < line.xValues.length; j++) {
                ctx.lineTo(line.xValues[j] * scaleX, targetHeight - line.yValues[j] * scaleY);
            }

            ctx.stroke();
        }

        return canvas.toDataURL('image/png');
    }

    public destroy() {
        this.isDestroyed = true;

        // 清理 Focus+Context 相关资源
        if (this.focusContextFilter) {
            this.focusContextFilter.destroy();
            this.focusContextFilter = undefined;
        }
        if (this.densityDataTexture) {
            this.densityDataTexture.destroy(true);
            this.densityDataTexture = undefined as any;
        }
        if (this.messinessTexture) {
            this.messinessTexture.destroy(true);
            this.messinessTexture = undefined as any;
        }

        if (this.densityTexture) {
            this.densityTexture.destroy(true);
        }

        if (this.viewport) {
            this.viewport.destroy({ children: true, texture: true });
        }

        if (this.app) {
            this.app.destroy(false, { children: true, texture: true });
        }
    }

    /**
     * 获取渲染器信息
     */
    public async getRendererInfo(): Promise<{
        type: string;
        gpu?: string;
        features?: string[];
    }> {
        const info: any = {
            type: this.app.renderer.type,
        };

        const hasWebGPU = await isWebGPUSupported();
        // WebGPU 特定信息
        if (hasWebGPU) {
            // @ts-ignore - WebGPU 特定属性
            const adapter = (this.app.renderer as any).gpu?.adapter;
            if (adapter) {
                info.gpu = adapter.name || 'Unknown GPU';
                info.features = Array.from(adapter.features || []);
            }
        }

        return info;
    }
}
